import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import { Info } from 'lucide-react';
import partnerAdminService from '../../services/partnerAdminService';
import LLMKeyMatrix from '../shared/LLMKeys/LLMKeyMatrix';
import {
  buildKeyRows,
  normalizeCredentials,
  resolvePartnerOwn,
  resolvePartnerChain,
} from '../shared/LLMKeys/llmKeyStatus';
import { KEYS_COPY, PARTNER_ADMIN_COPY } from '../../constants/partnerCopy';
import { describeApiError } from '../../utils/apiError';
import styles from '../PlatformAdmin/PlatformAdmin.module.scss';

// The platform decides the partner's fallback; show it read-only (§5.5 A).
const OWN_FALLBACK = {
  mode: 'readonly',
  label: 'Platform fallback',
  tooltip: KEYS_COPY.partnerFallbackReadOnlyTooltip,
  text: (row) => KEYS_COPY.partnerFallbackReadOnly(row.allowFallback),
};
const HIDDEN = { mode: 'hidden' };

/**
 * LLM keys (Partner tier guide §5.5), two screens:
 *   A. The partner's own keys — used by direct users and inherited by client
 *      companies. PUT takes only api_key/base_url; clearing is a DELETE.
 *   B. A client company's keys + inheritance. A provider with no row inherits
 *      the partner's key (the default). Badges follow company → partner →
 *      platform, which is why B needs A's rows.
 * Key changes can take up to 5 minutes to apply (server caches).
 */
const PartnerLLMKeysPanel = ({ ctx, notify }) => {
  const { companies, partnerName, reloadCompanies } = ctx;
  const [providers, setProviders] = useState([]);
  const [ownCreds, setOwnCreds] = useState([]);
  const [loading, setLoading] = useState(true);

  const [companyId, setCompanyId] = useState('');
  const [companyCreds, setCompanyCreds] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);

  const loadOwn = useCallback(async () => {
    try {
      setOwnCreds(normalizeCredentials(await partnerAdminService.getOwnKeys()));
    } catch (err) {
      notify(describeApiError(err, "Failed to load your partner's keys."), 'error');
    }
  }, [notify]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [providerList] = await Promise.all([partnerAdminService.listProviders(), loadOwn()]);
        if (!cancelled) setProviders(Array.isArray(providerList) ? providerList : []);
      } catch (err) {
        if (!cancelled) notify(describeApiError(err, 'Failed to load providers.'), 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadOwn, notify]);

  useEffect(() => {
    if (companyId === '' && companies.length) setCompanyId(companies[0].id);
  }, [companies, companyId]);

  const loadCompany = useCallback(async () => {
    if (companyId === '' || companyId == null) return;
    try {
      setCompanyLoading(true);
      setCompanyCreds(normalizeCredentials(await partnerAdminService.getCompanyKeys(companyId)));
    } catch (err) {
      if (err?.status === 404) {
        notify(PARTNER_ADMIN_COPY.noLongerInPartner(partnerName), 'warning');
        setCompanyId('');
        reloadCompanies();
      } else {
        notify(describeApiError(err, "Failed to load the company's keys."), 'error');
      }
    } finally {
      setCompanyLoading(false);
    }
  }, [companyId, notify, partnerName, reloadCompanies]);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  const ownRows = useMemo(() => buildKeyRows(providers, ownCreds), [providers, ownCreds]);
  const companyRows = useMemo(() => buildKeyRows(providers, companyCreds), [providers, companyCreds]);
  const ownByProvider = useMemo(() => new Map(ownCreds.map((c) => [c.provider_id, c])), [ownCreds]);

  const company = companies.find((c) => c.id === companyId) || null;
  const companyName = company?.name || 'this company';

  const resolveCompany = useCallback(
    (row) => resolvePartnerChain(row, ownByProvider.get(row.providerId), { partnerName, companyName }),
    [ownByProvider, partnerName, companyName]
  );

  const companyInherit = useMemo(
    () => ({
      mode: 'toggle',
      label: 'Use partner key',
      tooltip: () => KEYS_COPY.inheritToggle(partnerName, companyName),
    }),
    [partnerName, companyName]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* A. Own keys */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{partnerName}'s keys</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Used by your direct users, and by every company that inherits them.
          </Typography>
        </Box>
        <LLMKeyMatrix
          rows={ownRows}
          resolveEffective={resolvePartnerOwn}
          keyNoun="partner key"
          fallback={OWN_FALLBACK}
          inherit={HIDDEN}
          openaiBlockedText="Off right now — direct users and inheriting companies can't upload files or run knowledge-base agents until an OpenAI key is available."
          clearConfirmText={(row) => KEYS_COPY.clearPartnerKey(partnerName, row.name)}
          savedMessage={() => KEYS_COPY.saved}
          onSetKey={async (row, payload) => {
            await partnerAdminService.setOwnKey(row.providerId, payload);
            await loadOwn();
          }}
          onClearKey={async (row) => {
            await partnerAdminService.clearOwnKey(row.providerId);
            await loadOwn();
          }}
          notify={notify}
        />
      </Box>

      {/* B. A client company's keys + inheritance */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Company keys</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            A company uses its own key first, then yours (unless you turn inheritance off for that provider).
          </Typography>
        </Box>

        {companies.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            You have no companies yet.
          </Typography>
        ) : (
          <>
            <Box className={styles.toolbar}>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>Company</InputLabel>
                <Select label="Company" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                  {companies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box className={styles.noteBanner}>
              <Info size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                A provider {companyName} has no row for inherits your key — that's the default, so providers
                added later work without setup.
              </span>
            </Box>
            {companyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                <CircularProgress color="primary" />
              </Box>
            ) : (
              <LLMKeyMatrix
                rows={companyRows}
                resolveEffective={resolveCompany}
                keyNoun="company key"
                fallback={HIDDEN}
                inherit={companyInherit}
                openaiBlockedText={`Off right now — ${companyName} can't upload files or run knowledge-base agents until an OpenAI key is available.`}
                clearConfirmText={() => `The company key is removed; its inheritance setting is kept.`}
                savedMessage={() => KEYS_COPY.saved}
                onSetKey={async (row, payload) => {
                  await partnerAdminService.setCompanyKey(companyId, row.providerId, payload);
                  await loadCompany();
                }}
                onClearKey={async (row) => {
                  await partnerAdminService.clearCompanyKey(companyId, row.providerId);
                  await loadCompany();
                }}
                onToggleInherit={async (row, next) => {
                  await partnerAdminService.setCompanyKey(companyId, row.providerId, { inherit_from_partner: next });
                  await loadCompany();
                  notify(KEYS_COPY.saved, 'success');
                }}
                notify={notify}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default PartnerLLMKeysPanel;
